import tokenStore from '@polleverywhere/core/lib/ajax/csrf/token_store'
import URI from 'urijs'
import { rewriteToProxyIfNeeded } from './helpers'
import { refreshToken } from './csrf'

async function doFetchWithCSRF (url, options) {
  // Set the CSRF token when required and available
  if (!/^(get|head|options)$/i.test(options.method) && tokenStore.has(url)) {
    options.headers['X-CSRF-Token'] = tokenStore.get(url)
  }

  const response = await fetch(url.toString(), options)

  // Extract and store the new CSRF token when provided
  if (response.headers.has('X-CSRF-Token')) {
    tokenStore.set(url, response.headers.get('X-CSRF-Token'))
  }

  return response
}

async function isCSRFError (response) {
  return response.status === 422 && (await response.clone().json()).error?.type === 'InvalidAuthenticityToken'
}

/**
 * Wrapper around native Fetch for calling our PE APIs to include:
 * - CSRF handling
 * - nginx/alb proxying
 * - retries
 * @param {URI|string} resource PE Rails API path
 * @param {Object} [options] Fetch API options with additions noted below
 * @param {Number} [options.retries=0] How many times to retry on network or server errors
 * @returns {Response}
 */
export async function peFetch (resource, { method = 'GET', body, headers: extraHeaders = {}, retries = 0, ...fetchOpts } = {}) {
  const url = rewriteToProxyIfNeeded(URI(resource))
  const options = {
    method,
    credentials: 'include',
    headers: { ...extraHeaders },
    body,
    mode: 'cors',
    ...fetchOpts
  }

  let response = await doFetchWithCSRF(url, options)

  // If we failed due to CSRF - always fetch a new token and retry
  if (await isCSRFError(response)) {
    await refreshToken()
    response = await doFetchWithCSRF(url, options)
  }

  // Retry if needed and allowed
  while (retries > 0 && [0, 500].includes(response.status)) {
    retries -= 1
    await new Promise(resolve => setTimeout(resolve, 100))
    response = await doFetchWithCSRF(url, options)
  }

  return response
}

export { apiUrlFor } from './helpers'
